import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import "./index.scss";
import {
  stepperVariants,
  stepVariants,
  stepContentInnerVariants
} from "../../assets/framer/styles";
import step1SelectedIcon from "../../assets/icons/step1-selected.svg";
import step2Icon from "../../assets/icons/step2.svg";
import step2SelectedIcon from "../../assets/icons/step2-selected.svg";
import step3Icon from "../../assets/icons/step3.svg";
import step3SelectedIcon from "../../assets/icons/step3-selected.svg";
import step4Icon from "../../assets/icons/step4.svg";
import step4SelectedIcon from "../../assets/icons/step4-selected.svg";
import step5Icon from "../../assets/icons/step5.svg";
import successIcon from "../../assets/icons/success.svg";
import { useTranslation } from "react-i18next";
import { getCanonicalTemplateKey, isDhulHijjahTemplateKey } from "../../utils/templateUtils";

/*
  Instead of storing the text directly, we store translation keys.
  We'll call t(...) on them below.
*/
const stepData = [
  {
    icon: null,
    selectedIcon: step1SelectedIcon,
    titleKey: "stepper1Title",
    descriptionKey: "stepper1Description"
  },
  {
    icon: step2Icon,
    selectedIcon: step2SelectedIcon,
    titleKey: "stepper2Title",
    descriptionKey: "stepper2Description"
  },
  {
    icon: step3Icon,
    selectedIcon: step3SelectedIcon,
    titleKey: "stepper3Title",
    descriptionKey: "stepper3Description"
  },
  {
    icon: step4Icon,
    selectedIcon: step4SelectedIcon,
    titleKey: "stepper4Title",
    descriptionKey: "stepper4Description"
  },
  {
    icon: step5Icon,
    selectedIcon: null, // final step
    titleKey: "stepper5Title",
    descriptionKey: "stepper5Description"
  }
];

const CustomStepper = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const stage = parseInt(searchParams.get("stage") || "1", 10);
  const templateKey = getCanonicalTemplateKey(searchParams.get("template"));
  const isDhulHijjahTemplate = isDhulHijjahTemplateKey(templateKey);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, [stage]);

  const handleStepClick = (stepNumber) => {
    if (stage >= stepNumber) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("stage", stepNumber);
      setSearchParams(newParams);
    }
  };

  return (
    <motion.div
      className="custom-stepper"
      variants={stepperVariants}
      initial="normal"
      animate="normal"
    >
      {stepData.map((step, index) => {
        const isActive = stage === index + 1;
        const isSuccess = stage > index + 1;

        return (
          <motion.div
            key={index}
            className={`step ${
              isActive ? "active" : isSuccess ? "success" : "inactive"
            }`}
            variants={stepVariants}
            onClick={() => handleStepClick(index + 1)}
          >
            <div className="step-icon">
              <img
                src={
                  // If final step is active => success icon
                  stage === stepData.length && isActive
                    ? successIcon
                    : // If this step is the active one
                    isActive
                    ? step.selectedIcon
                    : // If user has passed it
                    isSuccess
                    ? successIcon
                    : // Otherwise default icon
                      step.icon
                }
                alt={`Step ${index + 1} icon`}
              />
            </div>
            <motion.div
              className="step-content"
              variants={stepContentInnerVariants}
              whileHover={isActive || isSuccess ? "hover" : ""}
            >
              {/* 
                Display "STEP" from i18n 
                e.g. "STEP 1" or "ÉTAPE 1" 
              */}
              <p className="step-number">
                {t("stepperStep")} {index + 1}
              </p>

              {/* Title & Description from translation keys */}
              <p className={`step-title ${isActive ? "active" : "inactive"}`}>
                {t(
                  isDhulHijjahTemplate
                    ? `${step.titleKey}DhulHijjah`
                    : step.titleKey
                )}
              </p>
              <p className="step-description">
                {t(
                  isDhulHijjahTemplate
                    ? `${step.descriptionKey}DhulHijjah`
                    : step.descriptionKey
                )}
              </p>
            </motion.div>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default CustomStepper;
